import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { AlertInfo } from '../../../../../models/alert-info';
import { SavedDataInfo } from '../../../../../models/form/saved-data-info';
import { AlertNotificationService } from '../../../../../services/alert-notification.service';
import { ItemsService } from '../../../../../services/api/general-settings/items.service';
import { FormService } from '../../../../../services/form.service';
import { ValidatorService } from '../../../../../services/validator.service';
import { InputFieldComponent } from '../../../../../components/form/input-field/input-field.component';
import { NgbAlert } from '@ng-bootstrap/ng-bootstrap';
import { SelectFieldComponent } from '../../../../../components/form/select-field/select-field.component';

import { Subscription, combineLatest } from 'rxjs';

@Component({
  selector: 'items-form',
  imports: [NgbAlert, InputFieldComponent, SelectFieldComponent, ReactiveFormsModule],
  templateUrl: './items-form.component.html',
  styleUrl: './items-form.component.scss'
})
export class ItemsFormComponent implements OnInit, OnChanges, OnDestroy {

  @Input() item?: any;
  @Output() onSavedData = new EventEmitter<SavedDataInfo>();

  form!: FormGroup;

  alertInfo: AlertInfo = { show: false, type: '', message: '' };
  itemProductsOptions: { key: number; value: string }[] = [];
  jurisdictionsOptions: { key: number; value: string }[] = [];
  itemTypesOptions: { key: number; value: string }[] = [];

  private dataLoaded = false;
  private marginCalcSetup = false;

  private subscriptions: Subscription[] = [];

  constructor(
    private validatorService: ValidatorService,
    private formService: FormService,
    private alertNotificationService: AlertNotificationService,
    private itemsService: ItemsService
  ) {
    this.itemsService.getItemProducts();
    this.itemsService.getItemTypes();
    
    setTimeout(() => {
      this.subscriptions.push(
      combineLatest([
        this.itemsService.itemProducts$,
        this.itemsService.jurisdictions$,
        this.itemsService.itemTypes$
      ]).subscribe(([itemProducts, jurisdictions, itemTypes]: any[]) => {
        this.itemProductsOptions = (itemProducts || []).map((p: any) => ({ key: Number(p.itemProductId), value: p.name }));
        this.jurisdictionsOptions = (jurisdictions || []).map((j: any) => ({ key: Number(j.jurisdictionId), value: j.name }));
        this.itemTypesOptions = (itemTypes || []).map((t: any) => ({ key: Number(t.itemTypeId), value: t.name }));
        this.dataLoaded = true;
        this.tryPopulateForm();
      })
    );
    }, 300);
    
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      code: new FormControl('', Validators.required),
      itemProductId: new FormControl('', Validators.required),
      itemTypeId: new FormControl('', Validators.required),
      jurisdictionId: new FormControl('', Validators.required),
      price: new FormControl('', [Validators.required, this.validatorService.numberValidator()]),
      maxDiscountPercent: new FormControl('', [Validators.required, this.validatorService.numberValidator()]),
      operatingCost: new FormControl('', [Validators.required, this.validatorService.numberValidator()]),
      introducerPercent: new FormControl('', [Validators.required, this.validatorService.numberValidator()]),
      margin: new FormControl({ value: '', disabled: true }, this.validatorService.numberValidator()),
    });
    this.tryPopulateForm();

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['item'] && changes['item'].currentValue) {
      this.tryPopulateForm();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private tryPopulateForm(): void {
    if (!this.form) return;
    if (!this.item) return;
    if (!this.dataLoaded) return;

    this.form.patchValue({
      code: this.item.code,
      itemProductId: Number(this.item.itemProduct?.itemProductId ?? this.item.itemProductId),
      itemTypeId: Number(this.item.itemType?.itemTypeId ?? this.item.itemTypeId),
      jurisdictionId: Number(this.item.jurisdiction?.jurisdictionId ?? this.item.jurisdictionId),
      price: Number(this.item.price),
      maxDiscountPercent: Number(this.item.maxDiscountPercent),
      operatingCost: Number(this.item.operatingCost),
      introducerPercent: Number(this.item.introducerPercent),
      margin: Number(this.item.margin ?? this.calculateMargin())
    });

    if (!this.marginCalcSetup) {
      this.setupMarginCalculation();
      this.marginCalcSetup = true;
    }
  }

  private setupMarginCalculation(): void {
    const priceControl = this.form.get('price');
    const operatingCostControl = this.form.get('operatingCost');
    const introducerPercentControl = this.form.get('introducerPercent');

    const updateMargin = () => {
      const marginValue = this.calculateMargin();
      this.form.get('margin')?.setValue(marginValue, { emitEvent: false });
    };

    updateMargin();

    [priceControl, operatingCostControl, introducerPercentControl].forEach(ctrl => {
      if (ctrl) {
        this.subscriptions.push(
          ctrl.valueChanges.subscribe(() => updateMargin())
        );
      }
    });
  }

  private calculateMargin(): number {
    const price = Number(this.form.get('price')?.value) || 0;
    const operatingCost = Number(this.form.get('operatingCost')?.value) || 0;
    const introducerPercent = Number(this.form.get('introducerPercent')?.value) || 0;

    const margin = price - operatingCost - (price * introducerPercent / 100);

    return Math.round(margin * 100) / 100;
  }

  convertFormValuesToNumbers(formValues: any): any {
    const numericFields = ['itemProductId', 'itemTypeId', 'jurisdictionId', 'price', 'maxDiscountPercent', 'operatingCost', 'introducerPercent', 'margin'];
    const result = { ...formValues };

    numericFields.forEach(field => {
      if (result[field] !== null && result[field] !== undefined && result[field] !== '') {
        result[field] = Number(result[field]);
      }
    });

    return result;
  }

  /**
   * *******************
   * Submit form methods
   * *******************
   */
  showAlertInfo(type: string, message: string) {
    this.alertInfo = {
      show: true,
      type,
      message
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => {
      this.alertInfo.show = false;
    }, 3000);
  }
  updateValidationErrors(validationErrors: any) {
    this.formService.resetErrors(this.form);
    this.formService.setValidationErrors(this.form, validationErrors);
  }
  submitFormSuccess(response: any) {
    this.alertNotificationService.success('Operation completed successfully')
    this.showAlertInfo('success', 'Operation completed successfully');

    this.onSavedData.emit({
      success: true,
      response
    });
  }
  submitFormError(error: any) {

    if (error.error.error.description == 'ARGUMENT_VALIDATION_ERROR') {
      this.updateValidationErrors(error.error.error.validationErrors);
    }
    else if (error.error.error.description == 'USER_EMAIL_ALREADY_EXISTS') {
      this.updateValidationErrors([{ field: 'email', defaultMessage: error.error.error.message }]);
      this.showAlertInfo('danger', `Please fix the error: ${error.error.error.message}`);
    }
    else {
      this.showAlertInfo('danger', error.error.error.message);
    }

    this.onSavedData.emit({
      success: false,
      error
    });

  }
  submitForm(): void {
    Object.keys(this.form.controls).forEach(key => {
      const control = this.form.get(key);
      control?.markAsTouched();
      control?.updateValueAndValidity();
    });

    if (this.form.valid) {
      const formValues = this.form.getRawValue();
      const convertedValues = this.convertFormValuesToNumbers(formValues);

      const values = Object.assign(
        convertedValues,
        {
          tenantId: 1,
          margin: this.calculateMargin()
        },
      );

      this.alertInfo.show = false;
      this.formService.resetErrors(this.form);

      const serviceMethod = this.item ? this.itemsService.update(this.item.itemId, values) : this.itemsService.create(values);
      serviceMethod.subscribe({
        next: (response: any) => {
          this.submitFormSuccess(response)
        },
        error: (error: any) => {
          this.submitFormError(error)
        }
      })
    }
  }
  /**
   * *******************
   * Submit form methods
   * *******************
   */


  getItemCodeInput(): FormControl { return this.getFormControl('code'); }
  getItemProductInput(): FormControl { return this.getFormControl('itemProductId'); }
  getItemTypeInput(): FormControl { return this.getFormControl('itemTypeId'); }
  getItemJurisdictionInput(): FormControl { return this.getFormControl('jurisdictionId'); }
  getItemPriceInput(): FormControl { return this.getFormControl('price'); }
  getItemMaxDiscountInput(): FormControl { return this.getFormControl('maxDiscountPercent'); }
  getItemOperatingCostInput(): FormControl { return this.getFormControl('operatingCost'); }
  getItemIntroducerInput(): FormControl { return this.getFormControl('introducerPercent'); }
  getItemMarginInput(): FormControl { return this.getFormControl('margin'); }

  getFormControl(name: string): FormControl { return this.form.get(name) as FormControl; }
}
